// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import * as React from "react";
import { useTranslations } from "next-intl";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Dialog, FormField, Input, useToast } from "@chotca/ui";
import type { UserMe } from "@chotca/sdk";
import { getSdk } from "@chotca/api-client";
import { qk } from "@chotca/api-client";

type Props = {
  open: boolean;
  onClose: () => void;
  user: UserMe;
};

const formSchema = z.object({
  fullName: z
    .string()
    .trim()
    .min(1, "Vui lòng nhập họ và tên")
    .max(120, "Họ và tên tối đa 120 ký tự"),
  jobTitle: z.string().trim().max(120, "Chức danh tối đa 120 ký tự"),
});

type FormValues = z.infer<typeof formSchema>;

export function EditProfileDialog({ open, onClose, user }: Props) {
  const t = useTranslations("profile");
  const tCommon = useTranslations("common");
  const { toast } = useToast();
  const qc = useQueryClient();

  const ep = user.employerProfile;
  const defaults = React.useMemo<FormValues>(
    () => ({ fullName: ep?.fullName ?? "", jobTitle: ep?.jobTitle ?? "" }),
    [ep?.fullName, ep?.jobTitle],
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "onTouched",
    defaultValues: defaults,
  });

  // Re-seed whenever the dialog (re)opens so it reflects the latest profile
  // and clears stale validation state.
  React.useEffect(() => {
    if (open) reset(defaults);
  }, [open, defaults, reset]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) =>
      getSdk().users.patchEmployer({
        fullName: values.fullName,
        jobTitle: values.jobTitle || null,
      }),
    onSuccess: () => {
      toast({ tone: "success", title: t("saveSuccess") });
      qc.invalidateQueries({ queryKey: qk.me });
      onClose();
    },
    onError: () => {
      toast({ tone: "error", title: t("saveError") });
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("title")}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            {tCommon("cancel")}
          </Button>
          <Button
            onClick={submit}
            disabled={mutation.isPending}
            loading={mutation.isPending}
          >
            {tCommon("save")}
          </Button>
        </div>
      }
    >
      <form className="grid gap-4 sm:grid-cols-2" onSubmit={submit} noValidate>
        <FormField label={t("fieldFullName")} error={errors.fullName?.message}>
          <Input
            invalid={!!errors.fullName}
            placeholder={t("fieldFullName")}
            autoFocus
            {...register("fullName")}
          />
        </FormField>
        <FormField label={t("fieldJobTitle")} error={errors.jobTitle?.message}>
          <Input
            invalid={!!errors.jobTitle}
            placeholder={t("fieldJobTitle")}
            {...register("jobTitle")}
          />
        </FormField>
        <FormField label={t("fieldEmail")}>
          <Input value={user.email ?? ""} readOnly />
        </FormField>
        {/* Native submit so Enter works; the footer button submits too. */}
        <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
      </form>
    </Dialog>
  );
}
